import { UntypedFormGroup } from '@angular/forms';
import { formatDateForPrinter } from '../../helpers/date.helper';
import { formatDateForBackend } from '../../helpers/util';
import { Address, AddressDTO } from './address';
import { Base } from './base';
import { Customer, CustomerDTO } from './customer';
import { Rider, RiderDTO } from './riders';
import { ProductInOrder, ProductInOrderDTO } from './product-in-order';
import { ZonesDTO } from './zone';

export type SweetStatus = 'pending' | 'accepted' | 'delivered';
export type PaymentType = "cash" | "pos";

export interface OrdersFilters {
  tenant_id?: number;
  onlyCurrentTenant?: boolean;
}

export interface OrderDTO {
  id?: number;
  amount?: number;
  delivery_date?: string;
  delivery_time?: string;
  notes?: string;
  address?: AddressDTO;
  address_id?: number;
  customer?: CustomerDTO;
  customer_id?: number;
  discount?: number;
  rider_id?: number;
  rider?: RiderDTO;
  status?: string;
  products_in_order?: ProductInOrderDTO[];
  eat_here?: boolean;
  sweet_status?: SweetStatus;
  deleted_at?: string;
  tenant_id?: number;
  payment_type?: PaymentType;
  shipping_cost?: number
}

export interface PrintableOrder {
  dailyProgressiveID?: number;
  amount?: number;
  discount?: number;
  totalQuantity?: number;
  address?: AddressDTO;
  zone?: ZonesDTO;
  customer?: CustomerDTO;
  deliveryDate?: string;
  deliveryTime?: string;
  printDate?: string;
  printTime?: string;
  notes?: string;
  productsInOrder?: ProductInOrderDTO[];
  isEdited?: boolean;
  shippingCost?: number;
  eatHere?: boolean;
  tenant_id?: number;
  payment_type?: PaymentType;
}

export class Order extends Base {
  dailyID: number;
  amount: number;
  discount: number;
  deliveryDate: Date;
  deliveryTime: string;
  notes: string;
  address: Address;
  customer: Customer;
  rider: Rider;
  status: string;
  productsInOrder: Array<ProductInOrder>;
  eatHere: boolean;
  infoWindowVisible: boolean;
  shippedAt: Date;
  deliveredAt: Date;
  sweetStatus: SweetStatus;
  tenant_id?: number;
  payment_type?: PaymentType;
  shipping_cost?: number



  constructor(source: any) {
    super(source);
    if (source) {
      this.dailyID = source.daily_id;
      this.amount = source.amount;
      this.discount = source.discount;
      this.deliveryDate = new Date(source.delivery_date);
      this.deliveryTime = source.delivery_time;
      this.notes = source.notes;
      this.address = source.address ? new Address(source.address) : null;
      this.customer = source.customer ? new Customer(source.customer) : null;
      this.rider = source.rider ? new Rider(source.rider) : null;
      // this.status = source.status ? new Status(source.status) : null;
      this.status = source.status,
      this.productsInOrder = new Array<ProductInOrder>();
      if (source.products_in_order) {
        source.products_in_order.forEach(product => {
          this.productsInOrder.push(new ProductInOrder(product));
        });
      }
      this.eatHere = source.eat_here ? source.eat_here : false;
      this.infoWindowVisible = false;
      if (source.shipped_at) {
        this.shippedAt = new Date(source.shipped_at);
      }
      if (source.delivered_at) {
        this.deliveredAt = new Date(source.delivered_at);
      }
      this.sweetStatus = source.sweet_status || 'pending';

      this.tenant_id = source.tenant_id
      this.payment_type = source.payment_type
      this.shipping_cost = source.shipping_cost
    }
  }

  // private getShippingCost(): number {
  //   if (this.address && this.address.zone) {
  //     return Number(this.address.zone.shipping_cost) || 0;
  //   }
  //   return 0;
  // }


  updateAmount() {
    this.amount = 0;

    // if (this.address) {
    //   this.amount += Number(this.getShippingCost());
    // }

    if (this.productsInOrder) {
      this.productsInOrder.forEach(p => {
        this.amount = Number(this.amount) + Number(p.amount);
      });
    }
    this.amount = this.amount * ((100 - this.discount) / 100);
  }

  static fromFormGroup(formGroup: UntypedFormGroup): Order {
    const formModel = formGroup.value;
    let order: Order = new Order(null);
    order.deliveryDate = formModel.date;
    //order.deliveryTime = formModel.time;
    order.deliveryTime = `${formModel.hour}:${formModel.minute}:00`;
    order.discount = formModel.discount;
    order.notes = formModel.notes;
    order.eatHere = formModel.eatHere;
    order.tenant_id = formModel.tenant_id;
    order.payment_type = formModel.payment_type;
    return order;
  }

  public toDTO(excludes?: string[]): OrderDTO {
    let dto: OrderDTO = {
      id: this.id,
      amount: this.amount,
      // discount: this.discount && this.discount > 0 ? Number(this.discount.toFixed(2)) : 0,
      discount: typeof this.discount === 'number' && !isNaN(this.discount) ? Number(this.discount.toFixed(2)) : 0,
      delivery_date: formatDateForBackend(this.deliveryDate),
      delivery_time: this.deliveryTime,
      notes: this.notes,
      eat_here: this.eatHere,
      tenant_id: this.tenant_id,
      payment_type: this.payment_type,
      shipping_cost: this.shipping_cost,
      sweet_status: this.sweetStatus,
      deleted_at: this.deletedAt ? formatDateForBackend(this.deletedAt) : null
    };

    //console.log(typeof this.discount, this.discount); // Debug per discount

    // if (this.status && !this.isToBeExcluded("status", excludes)) {
    //   dto.status = this.status.id;
    //   dto.status = this.status.toDTO();
    // }
    if (this.address && !this.isToBeExcluded("address", excludes)) {
      dto.address_id = this.address.id;
      dto.address = this.address.toDTO();
    }
    if (this.customer && !this.isToBeExcluded("customer", excludes)) {
      dto.customer_id = this.customer.id;
      dto.customer = this.customer.toDTO();
    }
    if (this.rider && !this.isToBeExcluded("rider", excludes)) {
      dto.rider_id = this.rider.id;
      dto.rider = this.rider.toDTO();
    }
    if (this.productsInOrder && !this.isToBeExcluded("productsInOrder", excludes)) {
      dto.products_in_order = [];
      this.productsInOrder.forEach(productInOrder => {
        dto.products_in_order.push(productInOrder.toDTO(["order"]));
      });
    }

    return dto;
  }

  public toPrintableOrder(isEdited: boolean): PrintableOrder {
    let printableOrder: PrintableOrder = {
      dailyProgressiveID: this.dailyID,
      amount: this.amount ? Number(Number(this.amount).toFixed(2)) : 0,
      discount: this.discount ? Number(Number(this.discount).toFixed(2)) : 0,
      totalQuantity: this.productsInOrder.map(p => p.quantity).reduce((totalQuantity, quantity) => totalQuantity + quantity),
      address: this.address ? this.address.toDTO() : null,
      zone: (this.address && this.address.zone) ? this.address.zone.toDTO() : null,
      customer: this.customer ? this.customer.toDTO() : null,
      deliveryDate: formatDateForPrinter(this.deliveryDate),
      deliveryTime: this.deliveryTime,
      printDate: formatDateForPrinter(new Date()),
      printTime: this.deliveryTime.substr(0, 5),
      notes: this.notes,
      isEdited: isEdited,
      eatHere: this.eatHere,
      //tenant_id: this.tenant_id,
      payment_type: this.payment_type
    };

    if (this.productsInOrder) {
      printableOrder.productsInOrder = [];
      let productsInOrder = this.productsInOrder;
      let drinksAndDesserts = productsInOrder.filter(p => p.product.type == 'Dessert' || p.product.type == 'Drink');
      let HamburgersAndSides = productsInOrder.filter(p => p.product.type != 'Dessert' && p.product.type != 'Drink');
      let orderedProducts = drinksAndDesserts.concat(HamburgersAndSides);
      orderedProducts.forEach(productInOrder => {
        printableOrder.productsInOrder.push(productInOrder.toDTO(["order"]));
      });
    }

    return printableOrder;
  }

  get archived(): boolean {
    return !!this.deletedAt;
  }

  get sweetsInOrder(): ProductInOrder[] {
    return this.productsInOrder ? this.productsInOrder.filter(inOrder => inOrder.product.family == 'gelato') : [];
  }

  get hasSweets(): boolean {
    return this.sweetsInOrder.length > 0;
  }
}
